import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover } from "react-tiny-popover";
import { DayPicker } from "react-day-picker";
import { useTranslate } from '@tolgee/react';
import { colors, rgb } from "../../Assets/styles/colors";
import { DATE_RANGE_PRESETS, MONTH_LABELS } from "../../Helpers/dateTimeHelper";
import "react-day-picker/style.css";

const PickerDayButton = ({ day, modifiers, dayHasEventFn, ...props }) => {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  const hasEvent = dayHasEventFn ? dayHasEventFn(day.date) : false;

  return (
    <button
      ref={ref}
      {...props}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
    >
      {props.children}
      {hasEvent && <EventDot />}
    </button>
  );
};

const DateRangePicker = ({
  presets = DATE_RANGE_PRESETS,
  disabledDirection = "future",
  dayHasEventFn,
  initialDateFrom,
  initialDateTo,
  onChange,
}) => {
  const { t } = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState(undefined);
  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(moment().year());
  const [selectedRange, setSelectedRange] = useState({
    dateFrom: initialDateFrom || null,
    dateTo: initialDateTo || null,
  });

  const { dateFrom, dateTo } = selectedRange;
  const hasValue = !!dateFrom && !!dateTo;

  const getActivePresetKey = () => {
    if (!hasValue) {
      return presets[0].labelKey;
    }

    return presets.find((preset) => {
      const range = preset.getValue();
      if (!range.from || !range.to) {
        return false;
      }
      return (
        moment(dateFrom).isSame(range.from, "day") &&
        moment(dateTo).isSame(range.to, "day")
      );
    })?.labelKey;
  };

  const activePresetKey = getActivePresetKey();

  const handleOpen = () => {
    setTempRange(hasValue ? { from: new Date(dateFrom), to: new Date(dateTo) } : undefined);
    setDisplayMonth(hasValue ? new Date(dateFrom) : new Date());
    setShowMonthYearPicker(false);
    setIsOpen(true);
  };

  const toggleMonthYearPicker = () => {
    setPickerYear(moment(displayMonth).year());
    setShowMonthYearPicker((prev) => !prev);
  };

  const handleMonthSelect = (monthIndex) => {
    setDisplayMonth(moment(displayMonth).year(pickerYear).month(monthIndex).toDate());
    setShowMonthYearPicker(false);
  };

  const goToPreviousYear = () => setPickerYear((year) => year - 1);
  const goToNextYear = () => setPickerYear((year) => year + 1);

  const ClickableCaptionLabel = (props) => (
    <span
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        toggleMonthYearPicker();
      }}
      style={{ cursor: "pointer" }}
    />
  );

  const handlePresetClick = (preset) => {
    const range = preset.getValue();
    const newDateFrom = range.from ? range.from.toISOString() : null;
    const newDateTo = range.to ? range.to.toISOString() : null;
    setSelectedRange({ dateFrom: newDateFrom, dateTo: newDateTo });
    onChange(newDateFrom, newDateTo);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (tempRange?.from && tempRange?.to) {
      const newDateFrom = moment(tempRange.from).startOf("day").toISOString();
      const newDateTo = moment(tempRange.to).endOf("day").toISOString();
      setSelectedRange({ dateFrom: newDateFrom, dateTo: newDateTo });
      onChange(newDateFrom, newDateTo);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const disabledMatcher = disabledDirection === "past" ? { before: new Date() } : { after: new Date() };

  const triggerLabel = () => {
    if (!hasValue) {
      return t(presets[0].labelKey);
    }

    if (moment(dateFrom).isSame(dateTo, "day")) {
      if (moment(dateFrom).isSame(moment(), "day")) return t('meetings.today');
      if (moment(dateFrom).isSame(moment().add(1, "day"), "day")) return t('meetings.tomorrow');
      return moment(dateFrom).format("MMM DD");
    }

    return `${moment(dateFrom).format("MMM DD")} – ${moment(dateTo).format("MMM DD")}`;
  };

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={() => setIsOpen(false)}
      positions={["bottom", "top"]}
      align="end"
      padding={8}
      content={
        <PickerContainer>
          <PresetsPanel>
            <PresetTitle>{t('meetings.date_range_quick_select')}</PresetTitle>
            {presets.map((preset) => (
              <PresetButton
                key={preset.labelKey}
                $active={activePresetKey === preset.labelKey}
                onClick={() => handlePresetClick(preset)}
              >
                {t(preset.labelKey)}
              </PresetButton>
            ))}
          </PresetsPanel>
          <CalendarPanel>
            <StyledCalendarWrapper>
              {showMonthYearPicker ? (
                <MonthYearPicker>
                  <YearNavRow>
                    <YearNavButton onClick={goToPreviousYear}>
                      <ChevronLeft size={16} />
                    </YearNavButton>
                    <YearLabel>{pickerYear}</YearLabel>
                    <YearNavButton onClick={goToNextYear}>
                      <ChevronRight size={16} />
                    </YearNavButton>
                  </YearNavRow>
                  <MonthsGridWrapper>
                    <MonthsGrid>
                      {MONTH_LABELS.map((monthLabel, monthIndex) => (
                        <MonthButton
                          key={monthLabel}
                          $selected={
                            moment(displayMonth).year() === pickerYear &&
                            moment(displayMonth).month() === monthIndex
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMonthSelect(monthIndex);
                          }}
                        >
                          {monthLabel}
                        </MonthButton>
                      ))}
                    </MonthsGrid>
                  </MonthsGridWrapper>
                </MonthYearPicker>
              ) : (
                <DayPicker
                  mode="range"
                  selected={tempRange}
                  onSelect={setTempRange}
                  disabled={disabledMatcher}
                  numberOfMonths={1}
                  month={displayMonth}
                  onMonthChange={setDisplayMonth}
                  navLayout="around"
                  components={{
                    DayButton: (dayButtonProps) => <PickerDayButton {...dayButtonProps} dayHasEventFn={dayHasEventFn} />,
                    CaptionLabel: ClickableCaptionLabel,
                  }}
                />
              )}
            </StyledCalendarWrapper>
            <ActionRow>
              <CancelButton onClick={handleCancel}>{t('general.cancel')}</CancelButton>
              <ApplyButton
                disabled={!tempRange?.from || !tempRange?.to}
                onClick={handleApply}
              >
                {t('general.apply')}
              </ApplyButton>
            </ActionRow>
          </CalendarPanel>
        </PickerContainer>
      }
    >
      <TriggerButton onClick={handleOpen}>
        <CalendarIcon size={16} />
        <span>{triggerLabel()}</span>
        <ChevronDown size={14} />
      </TriggerButton>
    </Popover>
  );
};

export default DateRangePicker;

const EventDot = styled.span`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.menu.selectedTextColor};
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 4px;
  background: ${({ theme }) => theme.menu.background};
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
`;

const PickerContainer = styled.div`
  display: flex;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
`;

const PresetsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  background: ${({ theme }) => theme.menu.background};
  border-right: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  min-width: 160px;
`;

const PresetTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  padding: 0 8px;
`;

const PresetButton = styled.button`
  padding: 10px 12px;
  background: ${({ theme, $active }) =>
    $active ? theme.chat.selectedBackground : "transparent"};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.menu.selectedTextColor : "transparent")};
  border-radius: 6px;
  color: ${({ theme, $active }) =>
    $active ? theme.menu.selectedTextColor : theme.menu.primaryTextColor};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.chat.selectedBackground};
    color: ${({ theme }) => theme.menu.selectedTextColor};
  }
`;

const CalendarPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 320px;
`;

const StyledCalendarWrapper = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;

  .rdp-root {
    --rdp-accent-color: ${({ theme }) => theme.menu.selectedTextColor};
    --rdp-day-height: 36px;
    --rdp-day-width: 36px;
    --rdp-day_button-height: 32px;
    --rdp-day_button-width: 32px;
    --rdp-day_button-border-radius: 8px;
    --rdp-day_button-border: none;
    --rdp-selected-border: none;
    --rdp-range_middle-background-color: ${({ theme }) => theme.chat.selectedBackground};
    --rdp-range_middle-color: ${({ theme }) => theme.menu.selectedTextColor};
    --rdp-range_start-date-background-color: ${({ theme }) => theme.menu.selectedTextColor};
    --rdp-range_end-date-background-color: ${({ theme }) => theme.menu.selectedTextColor};
    --rdp-range_start-color: ${({ theme }) => theme.menu.primaryTextColor};
    --rdp-range_end-color: ${({ theme }) => theme.menu.primaryTextColor};
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
  .rdp-months {
    width: 100% !important;
    max-width: 100% !important;
  }
  .rdp-month {
    width: 100%;
  }
  .rdp-month_grid {
    border-collapse: separate;
    border-spacing: 0 6px;
    margin: 0 auto;
  }
  .rdp-weekday {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
  }
  .rdp-day_button {
    position: relative;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
  .rdp-outside .rdp-day_button {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    opacity: 0.4;
  }
  .rdp-disabled .rdp-day_button {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    opacity: 0.3;
  }
  .rdp-day_button:hover:not([data-range-start="true"]):not([data-range-end="true"]):not([data-range-middle="true"]) {
    background: ${({ theme }) => theme.chat.selectedBackground};
    color: ${({ theme }) => theme.menu.selectedTextColor};
  }
  .rdp-today .rdp-day_button:not([data-range-start="true"]):not([data-range-end="true"]):not([data-selected-single="true"]) {
    box-shadow: inset 0 0 0 1px rgba(${rgb(colors.emotiiPurple)}, 0.4);
    color: ${({ theme }) => theme.menu.selectedTextColor};
    font-weight: 600;
  }
  .rdp-selected {
    font-weight: inherit;
    font-size: inherit;
  }
  .rdp-day_button[data-selected-single="true"] {
    background: ${({ theme }) => theme.menu.selectedTextColor};
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
  .rdp-button_previous,
  .rdp-button_next {
    color: ${({ theme }) => theme.menu.selectedTextColor};
  }
  .rdp-button_previous:disabled,
  .rdp-button_next:disabled {
    opacity: 0.3;
  }
  .rdp-button_previous:not(:disabled):hover,
  .rdp-button_next:not(:disabled):hover {
    background: ${({ theme }) => theme.chat.selectedBackground};
  }
  .rdp-caption_label {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-weight: 600;
  }
`;

const MonthYearPicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const MonthsGridWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const YearNavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const YearNavButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.chat.selectedBackground};
  }
`;

const YearLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  min-width: 64px;
  text-align: center;
`;

const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
`;

const MonthButton = styled.button`
  padding: 12px 16px;
  background: ${({ theme, $selected }) =>
    $selected ? theme.menu.selectedTextColor : "transparent"};
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.menu.selectedTextColor : theme.sidebar.borderColor)};
  border-radius: 8px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 13px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  margin-top: auto;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.background};
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const ApplyButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  background: ${({ theme }) => theme.menu.selectedTextColor};
  border: 1px solid ${({ theme }) => theme.menu.selectedTextColor};
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;
